import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DataTable,
  SearchInput,
  StandardPage,
  StandardToolbar,
  TableFooter,
  TablePagination,
  type DataTableColumn,
  type DataTableState,
  type SortDirection,
} from '@components/base';
import { HistoryCameraList } from '@components/HistoryCameraList';
import { RecordingClipPlayer } from '@components/RecordingClipPlayer';
import { useCameras } from '@hooks/useCameras';
import { useDebounced } from '@hooks/useDebounced';
import { useRecordings } from '@hooks/useRecordings';
import { getViewerUtcOffsetMinutes } from '@lib/date-ymd';
import { resolveRecordingMediaUrl } from '@api/recordings';
import type { Recording, RecordingListQuery } from '@api/types';

const PAGE_SIZE_DEFAULT = 25;

type RecordingSortBy = NonNullable<RecordingListQuery['sortBy']>;

function formatDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function clipLabel(row: Recording): string {
  if (row.fileName) return row.fileName;
  return row.name.endsWith('.mp4') ? row.name : `${row.name}.mp4`;
}

const FILTER_INPUT_CLASS =
  'h-9 rounded-md border px-2 text-sm focus-visible:outline-none focus-visible:ring-2';

export function RecordingsListView(): JSX.Element {
  const {
    data: camerasData,
    isLoading: camerasLoading,
    error: camerasError,
  } = useCameras({
    page: 1,
    pageSize: 100,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const cameras = camerasData?.items ?? [];
  const cameraNameById = useMemo(
    () => new Map(cameras.map((camera) => [camera.id, camera.name])),
    [cameras],
  );

  const [cameraId, setCameraId] = useState<number | undefined>(undefined);
  const [date, setDate] = useState('');
  const [playingRecording, setPlayingRecording] = useState<Recording | null>(null);
  const [playRequestToken, setPlayRequestToken] = useState(0);
  const playerAnchorRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 300);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sortBy, setSortBy] = useState<RecordingSortBy>('startTime');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const listQuery = useMemo((): RecordingListQuery => {
    const query: RecordingListQuery = {
      utcOffsetMinutes: getViewerUtcOffsetMinutes(),
      search: debouncedSearch || undefined,
      sortBy,
      sortOrder,
      page,
      pageSize,
    };
    if (cameraId !== undefined) query.cameraId = cameraId;
    if (date) query.date = date;
    return query;
  }, [cameraId, date, debouncedSearch, sortBy, sortOrder, page, pageSize]);

  const { data, isLoading, error, refresh } = useRecordings(listQuery);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const handleSortChange = useCallback((columnId: string, direction: SortDirection) => {
    setSortBy(columnId as RecordingSortBy);
    setSortOrder(direction);
    setPage(1);
  }, []);

  const handleCameraSelect = useCallback((nextId: number | undefined) => {
    setCameraId(nextId);
    setPage(1);
    setPlayingRecording(null);
  }, []);

  const handleDateChange = useCallback((nextDate: string) => {
    setDate(nextDate);
    setPage(1);
    setPlayingRecording(null);
  }, []);

  useEffect(() => {
    if (!playingRecording) return;
    if (cameraId !== undefined && playingRecording.cameraId !== cameraId) {
      setPlayingRecording(null);
    }
  }, [cameraId, playingRecording]);

  const handleRowClick = useCallback((row: Recording) => {
    setPlayingRecording(row);
    setPlayRequestToken((token) => token + 1);
  }, []);

  useEffect(() => {
    if (!playingRecording) return;
    playerAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [playingRecording, playRequestToken]);

  const playingCameraName =
    playingRecording != null
      ? cameraNameById.get(playingRecording.cameraId) ?? `Camera ${playingRecording.cameraId}`
      : '';

  const columns = useMemo((): DataTableColumn<Recording>[] => {
    const cols: DataTableColumn<Recording>[] = [
      {
        id: 'thumbnail',
        header: '',
        minWidth: 88,
        sortable: false,
        render: (row) =>
          row.thumbnailUrl ? (
            <img
              src={resolveRecordingMediaUrl(row.thumbnailUrl)}
              alt=""
              loading="lazy"
              decoding="async"
              className="h-12 w-20 rounded object-cover"
              style={{
                backgroundColor: 'var(--bui-surface-muted, var(--bui-border))',
              }}
            />
          ) : (
            <div
              className="flex h-12 w-20 items-center justify-center rounded text-[10px]"
              style={{
                backgroundColor: 'var(--bui-surface-muted, var(--bui-border))',
                color: 'var(--bui-text-placeholder)',
              }}
              aria-hidden
            >
              —
            </div>
          ),
      },
      {
        id: 'fileName',
        header: 'Clip',
        primary: true,
        minWidth: 220,
        sortable: false,
        render: (row) => clipLabel(row),
        cellClassName: 'font-mono text-xs',
      },
    ];

    if (cameraId === undefined) {
      cols.push({
        id: 'cameraId',
        header: 'Camera',
        minWidth: 140,
        render: (row) => cameraNameById.get(row.cameraId) ?? `Camera ${row.cameraId}`,
      });
    }

    cols.push(
      {
        id: 'startTime',
        header: 'Start',
        minWidth: 160,
        sortable: true,
        render: (row) => formatDateTime(row.startTime),
      },
      {
        id: 'durationSeconds',
        header: 'Duration',
        minWidth: 100,
        sortable: true,
        render: (row) => formatDuration(row.durationSeconds),
      },
      {
        id: 'fileSizeBytes',
        header: 'Size',
        minWidth: 90,
        align: 'right',
        sortable: true,
        render: (row) => formatFileSize(row.fileSizeBytes),
      },
    );

    return cols;
  }, [cameraId, cameraNameById]);

  const tableState: DataTableState = useMemo(() => {
    if (error) return 'error';
    if (isLoading && items.length === 0) return 'loading';
    if (!isLoading && items.length === 0 && debouncedSearch) return 'no-results';
    if (!isLoading && items.length === 0) return 'empty';
    return 'idle';
  }, [error, isLoading, items.length, debouncedSearch]);

  const filterSummary =
    cameraId !== undefined
      ? cameraNameById.get(cameraId) ?? `camera ${cameraId}`
      : 'all cameras';

  return (
    <div className="bui-scope flex h-full min-h-0">
      <HistoryCameraList
        cameras={cameras}
        selectedCameraId={cameraId}
        onSelect={handleCameraSelect}
        isLoading={camerasLoading}
        error={camerasError}
      />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <StandardPage
          title="History"
          infoTooltip="Recorded clips from storage folders. Select a camera on the left, then click a row to play."
          className="h-full"
          toolbar={
            <StandardToolbar
              left={
                <label
                  className="inline-flex items-center gap-2 text-sm"
                  style={{ color: 'var(--bui-text-secondary)' }}
                >
                  <span className="sr-only">Date</span>
                  <span aria-hidden>Date</span>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => handleDateChange(e.target.value)}
                    className={FILTER_INPUT_CLASS}
                    style={{
                      borderColor: 'var(--bui-border)',
                      backgroundColor: 'var(--bui-surface)',
                      color: 'var(--bui-text-primary)',
                    }}
                  />
                  {date ? (
                    <button
                      type="button"
                      onClick={() => handleDateChange('')}
                      className="text-xs underline-offset-2 hover:underline"
                      style={{ color: 'var(--bui-accent)' }}
                    >
                      All dates
                    </button>
                  ) : null}
                  <span className="text-xs" style={{ color: 'var(--bui-text-placeholder)' }}>
                    {filterSummary}
                  </span>
                </label>
              }
              right={
                <SearchInput
                  label="Search recordings"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search clips…"
                />
              }
            />
          }
          footer={
            <TableFooter
              total={total}
              pagination={
                <TablePagination
                  page={page}
                  pageSize={pageSize}
                  total={total}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                />
              }
            />
          }
        >
          {playingRecording ? (
            <div ref={playerAnchorRef}>
              <RecordingClipPlayer
                key={`${playingRecording.id}:${playRequestToken}`}
                recording={playingRecording}
                cameraName={playingCameraName}
                onClose={() => setPlayingRecording(null)}
              />
            </div>
          ) : null}

          <DataTable
            columns={columns}
            rows={items}
            getRowId={(row) => row.id}
            state={tableState}
            errorMessage={error ?? undefined}
            onRetry={refresh}
            sort={{ columnId: sortBy, direction: sortOrder }}
            onSortChange={handleSortChange}
            selectedRowId={playingRecording?.id ?? null}
            onRowClick={handleRowClick}
            emptyState={{
              message: `No recordings found for ${filterSummary}${date ? ` on ${date}` : ''}.`,
            }}
            noResultsState={{ message: 'No clips match your search.' }}
            ariaLabel="Recording history"
          />
        </StandardPage>
      </div>
    </div>
  );
}
